/**
 * sopKind.js — что это за DICOM-объект: снимок или не снимок.
 *
 * Зачем. В исследовании из рентген-операционной (Philips Azurion) рядом с
 * киносъёмкой лежат объекты Presentation State — «состояние показа». В них
 * записано, как показывать кадры: окно, поворот, метки. ПИКСЕЛЕЙ В НИХ НЕТ
 * ВООБЩЕ — ни строк, ни столбцов, ни данных изображения.
 *
 * Программа их не отличала и заводила каждый как отдельную СЕРИЮ. Врач видел
 * в панели пять серий «PR — Left Coronary 30 fps Low, 1 изобр.», нажимал — и
 * получал чёрное окно и ошибку. Пять пустых строк среди настоящих съёмок, и
 * все выглядят одинаково: понять, какая настоящая, можно только методом тыка.
 *
 * То же самое с протоколами облучения (RTDOSE/RTPLAN/RTSTRUCT), структурными
 * отчётами (SR) и пометками (KO): это файлы про снимки, а не снимки.
 *
 * Файл без импортов — чтобы правило проверялось тестом, а не запуском.
 */

/** Объекты БЕЗ пикселей. Показывать их как серию нельзя ни при каких условиях. */
export const NON_IMAGE_SOP = Object.freeze({
  '1.2.840.10008.5.1.4.1.1.11.1': 'Presentation State (ч/б)',
  '1.2.840.10008.5.1.4.1.1.11.2': 'Presentation State (цвет)',
  '1.2.840.10008.5.1.4.1.1.11.3': 'Presentation State (псевдоцвет)',
  '1.2.840.10008.5.1.4.1.1.11.4': 'Presentation State (наложение)',
  '1.2.840.10008.5.1.4.1.1.11.5': 'Presentation State (XA/XRF)',
  '1.2.840.10008.5.1.4.1.1.88.11': 'Структурированный отчёт (basic)',
  '1.2.840.10008.5.1.4.1.1.88.22': 'Структурированный отчёт (enhanced)',
  '1.2.840.10008.5.1.4.1.1.88.33': 'Структурированный отчёт (comprehensive)',
  '1.2.840.10008.5.1.4.1.1.88.34': 'Структурированный отчёт (3D)',
  '1.2.840.10008.5.1.4.1.1.88.40': 'Протокол процедуры',
  '1.2.840.10008.5.1.4.1.1.88.59': 'Отмеченные объекты (KO)',
  '1.2.840.10008.5.1.4.1.1.88.65': 'Отчёт дозы облучения',
  '1.2.840.10008.5.1.4.1.1.88.67': 'Отчёт дозы рентгена',
  '1.2.840.10008.5.1.4.1.1.481.1': 'RT Image',          // формально с пикселями, но это портал, не срез
  '1.2.840.10008.5.1.4.1.1.481.2': 'RTDOSE',
  '1.2.840.10008.5.1.4.1.1.481.3': 'RTSTRUCT',
  '1.2.840.10008.5.1.4.1.1.481.5': 'RTPLAN',
  '1.2.840.10008.5.1.4.1.1.481.8': 'RT Ion Plan',
  '1.2.840.10008.5.1.4.1.1.66': 'Raw Data',
  '1.2.840.10008.5.1.4.1.1.66.4': 'Сегментация',
  '1.2.840.10008.5.1.4.1.1.9.1.1': 'ЭКГ',
  '1.2.840.10008.5.1.4.1.1.9.1.2': 'ЭКГ (общая)',
  '1.2.840.10008.5.1.4.1.1.9.1.3': 'ЭКГ (амбулаторная)',
  '1.2.840.10008.5.1.4.1.1.104.1': 'Инкапсулированный PDF',
  '1.2.840.10008.5.1.4.1.1.104.2': 'Инкапсулированный CDA',
});

/**
 * Модальности, которые НЕ являются снимками пациента.
 * Нужны на случай, когда SOP Class потерян при выгрузке (бывает у старых АРМ).
 */
export const NON_IMAGE_MODALITY = Object.freeze(['PR', 'SR', 'KO', 'RTSTRUCT', 'RTPLAN', 'RTDOSE', 'RTRECORD', 'PLAN', 'DOC', 'AU', 'ECG']);

/**
 * Модальности, где кадры идут ВО ВРЕМЕНИ, а не в пространстве: киносъёмка.
 *
 * Отличать обязательно. У киносъёмки коронарных артерий все 233 кадра сняты
 * с ОДНОГО положения трубки: это один и тот же срез в разные моменты, а не
 * стопка срезов по телу. Строить из них объём и реформаты бессмысленно —
 * «толщина» такого объёма равна нулю, и в аксиальном окне выходит каша.
 * Такую серию надо крутить как кино, а не резать.
 */
export const CINE_MODALITY = Object.freeze(['XA', 'XRF', 'US', 'ES', 'IVUS']);

/**
 * @param {object} p
 * @param {string} [p.sopClassUID]
 * @param {string} [p.modality]
 * @param {number} [p.rows]
 * @param {number} [p.columns]
 * @returns {{isImage: boolean, kind: string, why: string}}
 */
export function classifySop({ sopClassUID = '', modality = '', rows = 0, columns = 0 } = {}) {
  const sop = String(sopClassUID || '').trim();
  const mod = String(modality || '').trim().toUpperCase();

  if (NON_IMAGE_SOP[sop]) {
    return { isImage: false, kind: NON_IMAGE_SOP[sop], why: 'этот тип объекта не содержит пикселей' };
  }
  if (NON_IMAGE_MODALITY.includes(mod)) {
    return { isImage: false, kind: `модальность ${mod}`, why: 'модальность не изображение' };
  }
  // Последний рубеж: у снимка обязаны быть размеры. Ноль строк или столбцов —
  // показывать нечего, каким бы ни был SOP Class.
  if ((rows === 0 || columns === 0) && (rows || columns || sop || mod)) {
    return { isImage: false, kind: 'без размеров кадра', why: 'нет Rows/Columns — пикселей нет' };
  }
  return { isImage: true, kind: mod || 'изображение', why: '' };
}

/**
 * Кино это или стопка срезов.
 *
 * @param {object} p
 * @param {string} [p.modality]
 * @param {number} [p.numberOfFrames]
 * @param {number} [p.spatialSpreadMm] разброс положений срезов, мм (если известен)
 * @returns {{isCine: boolean, why: string}}
 */
export function classifyCine({ modality = '', numberOfFrames = 1, spatialSpreadMm = null } = {}) {
  const mod = String(modality || '').trim().toUpperCase();
  const n = Math.max(1, Math.floor(numberOfFrames || 1));

  if (n <= 1) return { isCine: false, why: 'один кадр' };
  if (CINE_MODALITY.includes(mod)) {
    return { isCine: true, why: `${mod}: кадры идут во времени, а не по телу` };
  }
  // Разброс положений известен и он нулевой — кадры сняты с одного места.
  if (spatialSpreadMm !== null && spatialSpreadMm < 0.01) {
    return { isCine: true, why: 'все кадры с одного положения — это время, а не пространство' };
  }
  return { isCine: false, why: 'кадры разнесены по телу — это объём' };
}

export default { NON_IMAGE_SOP, NON_IMAGE_MODALITY, CINE_MODALITY, classifySop, classifyCine };
